export const LAST_UPDATED = '21 Oct 2026';

export type PhoneGroup = [series: string, models: string[]];

export type PhoneBrand = {
  id: string;
  name: string;
  prefix: string;
  note: string;
  groups: PhoneGroup[];
};

export const PHONE_SOURCES = [
  ['Apple: models that support eSIM', 'https://support.apple.com/en-om/guide/iphone/aside/iph97b3e34cd/26/ios/26'],
  ['Samsung: Galaxy eSIM support', 'https://www.samsung.com/au/support/mobile-devices/galaxy-esim-and-supported-network-carriers/'],
] as const;

export const BRANDS: PhoneBrand[] = [
  {
    id: 'ios',
    name: 'iPhone',
    prefix: 'iPhone',
    note: 'Per Apple: iPhones sold in mainland China support eSIM only on iPhone 17e and iPhone Air. Hong Kong and Macao models have exceptions. Check with *#06# if yours was bought there.',
    groups: [
      ['iPhone 17 series', ['17e', '17', '17 Pro', '17 Pro Max', 'Air']],
      ['iPhone 16 series', ['16e', '16', '16 Plus', '16 Pro', '16 Pro Max']],
      ['iPhone 15 series', ['15', '15 Plus', '15 Pro', '15 Pro Max']],
      ['iPhone 14 series', ['14', '14 Plus', '14 Pro', '14 Pro Max']],
      ['iPhone 13 series', ['13 mini', '13', '13 Pro', '13 Pro Max']],
      ['iPhone 12 series', ['12 mini', '12', '12 Pro', '12 Pro Max']],
      ['iPhone 11 series', ['11', '11 Pro', '11 Pro Max']],
      ['iPhone SE', ['SE (2nd gen)', 'SE (3rd gen)']],
      ['iPhone X series (2018)', ['XS', 'XS Max', 'XR']],
    ],
  },
  {
    id: 'samsung',
    name: 'Samsung Galaxy',
    prefix: 'Galaxy',
    note: 'Based on Samsung’s Australian model list. Models sold in other regions (including India) can differ, so confirm with *#06#.',
    groups: [
      ['Galaxy S26', ['S26', 'S26+', 'S26 Ultra', 'S26 FE']],
      ['Galaxy S25', ['S25', 'S25+', 'S25 Ultra', 'S25 Edge', 'S25 FE']],
      ['Galaxy S24', ['S24', 'S24+', 'S24 Ultra', 'S24 FE']],
      ['Galaxy S23', ['S23', 'S23+', 'S23 Ultra', 'S23 FE']],
      ['Galaxy S22', ['S22', 'S22+', 'S22 Ultra']],
      ['Galaxy S21', ['S21', 'S21+', 'S21 Ultra']],
      ['Galaxy S20', ['S20', 'S20+', 'S20 Ultra']],
      ['Galaxy Z Fold / Flip', ['Z Fold8 Ultra', 'Z Fold8', 'Z Flip8', 'Z Fold7', 'Z Flip7', 'Z Flip7 FE', 'Z Fold6', 'Z Flip6', 'Z Fold5', 'Z Flip5', 'Z Fold4', 'Z Flip4', 'Z Fold3', 'Z Flip3', 'Z Fold2', 'Z Flip 5G', 'Z Flip', 'Fold']],
      ['Galaxy A', ['A56 5G', 'A55 5G', 'A36 5G']],
      ['Galaxy Note', ['Note20', 'Note20 Ultra']],
      ['Galaxy XCover', ['XCover7', 'XCover7 Pro']],
    ],
  },
  {
    id: 'pixel',
    name: 'Google Pixel',
    prefix: 'Pixel',
    note: 'eSIM service can vary by country and carrier. Pixel phones aren’t officially supported in mainland China.',
    groups: [
      ['Pixel 10', ['10', '10 Pro', '10 Pro XL', '10 Pro Fold']],
      ['Pixel 9', ['9', '9 Pro', '9 Pro XL', '9 Pro Fold', '9a']],
      ['Pixel 8', ['8', '8 Pro', '8a']],
      ['Pixel 7', ['7', '7 Pro', '7a']],
      ['Pixel Fold', ['Fold']],
      ['Pixel 6', ['6', '6 Pro', '6a']],
      ['Pixel 5', ['5', '5a 5G']],
      ['Pixel 4', ['4', '4 XL', '4a', '4a 5G']],
      ['Pixel 3', ['3', '3 XL', '3a', '3a XL']],
    ],
  },
  {
    id: 'other',
    name: 'Other Android brands',
    prefix: '',
    note: 'Select models only, and support varies by region and variant. Honor, Nothing, Vivo and others also have eSIM models: use the dial code to be sure.',
    groups: [
      ['Motorola', ['Edge 50 Ultra', 'Edge 50 Pro', 'Edge 50 Fusion', 'Edge (2023)', 'Edge 40 Neo', 'Moto G Power (2024)', 'Moto G (2024)', 'Moto G34']],
      ['OnePlus', ['9 Pro', '10 Pro']],
      ['Oppo', ['Find X7 Ultra', 'Find N2 Flip', 'Find X6 Pro', 'Find X5 Pro', 'Find X3 Pro', 'Reno10 Pro+', 'Reno 6 Pro+ 5G', 'Reno 5 Pro+ 5G']],
      ['Xiaomi', ['13', '13 Pro', '13 Ultra', '12', '12 Pro', '12T Pro', 'Mi 11', 'Mi 11 Pro', 'Mi 11 Ultra', 'Redmi Note 12 Pro+', 'Redmi Note 14 Pro 5G', 'Redmi Note 14 Pro+ 5G']],
      ['Sony Xperia', ['1 III', '5 III', 'Pro-I']],
    ],
  },
];

export function normalizePhoneQuery(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9+]/g, '');
}

export function phoneName(brand: PhoneBrand, series: string, model: string): string {
  if (brand.id === 'other') return `${series} ${model}`;
  if (model === 'Fold' && brand.id === 'pixel') return 'Pixel Fold';
  return `${brand.prefix} ${model}`.trim();
}

export type PhoneMatch = {
  brand: PhoneBrand;
  groups: PhoneGroup[];
  count: number;
};

export function matchPhones(query: string): { brands: PhoneMatch[]; total: number } {
  const needle = normalizePhoneQuery(query);
  const brands: PhoneMatch[] = [];
  let total = 0;
  for (const brand of BRANDS) {
    const groups: PhoneGroup[] = [];
    let count = 0;
    for (const [series, models] of brand.groups) {
      const kept = models.filter((model) => {
        if (!needle) return true;
        return normalizePhoneQuery(phoneName(brand, series, model)).includes(needle)
          || normalizePhoneQuery(series).includes(needle);
      });
      if (kept.length === 0) continue;
      groups.push([series, kept]);
      count += kept.length;
    }
    if (count === 0) continue;
    brands.push({ brand, groups, count });
    total += count;
  }
  return { brands, total };
}
